import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  Input,
  NgZone, OnChanges, OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {BooleanInput, coerceBooleanProperty} from "@angular/cdk/coercion";
import {ActivatedRoute, NavigationEnd, Router, UrlSegment} from "@angular/router";
import {filter, Subscription} from "rxjs";

const CURRENT_ROUTING_ROOT_PATH: string = './';

@Component({
    selector: 'app-tab-group',
    templateUrl: './tab-group.component.html',
    styleUrls: ['./tab-group.component.scss'],
    standalone: false
})
export class TabGroupComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('tabContentGroup') tabContentGroup?: ElementRef;

  // 上位コンポーネントから特定のタブを選択したい際にindexを指定
  // また、双方向バインディングに対応させるために @Output() selectIdxChange を定義
  @Input() selectIdx?: number;
  @Output() selectIdxChange: EventEmitter<number> = new EventEmitter<number>();

  // <app-tab-group flexible> とパラメータを指定することでタブタイトルのリストをブロック要素内に全て収まる形で縮小表示する
  // ただ、指定したとしてもきれいに収まるかどうかは各タブのタイトルの長さ次第となるため画面表示確認を行いながら使用する
  @Input()
  get flexible(): boolean {
    return this.isFlexible;
  };
  set flexible(value: BooleanInput) {
    this.isFlexible = coerceBooleanProperty(value);
  }
  isFlexible: boolean = false;

  selectedTabIdx: number = 0;
  tabNameList: string[] = [];
  tabContentList: HTMLElement[] = [];
  tabRouterLinkList: string[] = [];
  isTabContentSetComplete: boolean = false;

  routeUrlSubscription?: Subscription;
  routerEventSubscription?: Subscription;

  // routerLinkオプションを指定したタブを開いた状態でページをリロードした場合、
  // リロード後に開いたタブを維持するために使用
  selectedTabRouterLinkByUrl?: string;
  urlSegment: UrlSegment[] = [];

  isClickTabChange: boolean = false;
  isAfterViewInit: boolean = false;
  isOnContentChange: boolean = false;

  constructor(
    private ngZone: NgZone,
    private changeDetectionRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    // リロード時に発火。リロードした際に適切にタブを保持するために使用
    this.routeUrlSubscription = this.route.url.subscribe((urlSegment: UrlSegment[]) => {
      this.urlSegment = urlSegment;

      this.selectedTabRouterLinkByUrl =
        this.router.url.slice(this.router.url.indexOf(urlSegment.join('/')) + urlSegment.join('/').length + 1)
          .split(/[\/?#:]/)[0];
    });

    // URL変更時に発火。主にブラウザバック時に適切にタブを切り替えるために使用
    this.routerEventSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        const url: string = e.url;
        this.selectedTabRouterLinkByUrl =
          url.slice(url.indexOf(this.urlSegment.join('/')) + this.urlSegment.join('/').length + 1)
            .split(/[\/?#:]/)[0];

        // setTabContent関数を重複して呼び出すとエラーが発生するため、重複実行を避けるための分岐
        if (!this.isClickTabChange && this.isAfterViewInit) {
          this.setTabContent();
        }
      });
  }

  ngOnInit(): void {
    if (this.selectIdx !== undefined) {
      this.selectedTabIdx = this.selectIdx;
    }
  }

  ngOnDestroy(): void {
    if (this.routeUrlSubscription) {
      this.routeUrlSubscription.unsubscribe();
    }
    if (this.routerEventSubscription) {
      this.routerEventSubscription.unsubscribe();
    }
  }

  ngOnChanges(): void {
    if (this.selectIdx !== undefined && this.isTabContentSetComplete) {
      this.changeTab(this.selectIdx);
    }
  }

  ngAfterViewInit(): void {
    this.setTabContent();
    this.isTabContentSetComplete = true;

    if (this.selectIdx !== undefined) {
      this.changeTab(this.selectIdx);
    }

    this.changeDetectionRef.detectChanges();
    this.isAfterViewInit = true;
  }

  setTabContent(): void {
    this.ngZone.run(() => {
      this.tabContentList = [...this.tabContentGroup?.nativeElement.children];

      this.tabNameList = [];
      this.tabRouterLinkList = [];
      this.tabContentList.map((tab: HTMLElement) => {
        tab.style.display = 'none';
        this.tabNameList.push(tab.dataset['label'] ?? '');
        this.tabRouterLinkList.push((tab.dataset['routerLink'] !== '' && tab.dataset['routerLink'] !== undefined) ? tab.dataset['routerLink'] : CURRENT_ROUTING_ROOT_PATH);
      });

      const selectedTabIdxTmp: number = this.tabRouterLinkList.findIndex((routerLink: string) => {
        return routerLink.includes(this.selectedTabRouterLinkByUrl!);
      });

      if (!this.isOnContentChange) {
        this.selectedTabIdx = selectedTabIdxTmp !== -1 ? selectedTabIdxTmp : 0;
      }

      if (this.tabContentList[this.selectedTabIdx]) {
        this.tabContentList[this.selectedTabIdx].style.display = 'block';
      }
    });
  }

  async changeTab(idx: number): Promise<void> {
    this.isClickTabChange = true;

    if (this.tabContentList[this.selectedTabIdx]) {
      this.tabContentList[this.selectedTabIdx].style.display = 'none';
    }

    if (this.tabContentList[idx]) {
      this.tabContentList[idx].style.display = 'block';
    }

    this.selectedTabIdx = idx;
    this.selectIdxChange.emit(idx);

    // タブグループの中に最低一つrouterLinkが指定されている場合はURL遷移によってタブの切替を行う。
    // 逆にタブグループの中に一つもrouterLinkが指定されていない場合（ポップアップ内でタブ切り替えを行うような場合）は
    // URL遷移させてしまうと意図した挙動にならないため、URL遷移は行わない
    if (this.tabRouterLinkList.find((routerLink: string) => routerLink !== CURRENT_ROUTING_ROOT_PATH)) {
      await this.router.navigate([this.tabRouterLinkList[idx]], {relativeTo: this.route});
    }

    this.isClickTabChange = false;
  }

  onContentChange(): void {
    this.isOnContentChange = true;
    this.setTabContent();
    this.isOnContentChange = false;
  }
}
